import React, { useState } from 'react';
import AMPageShell from './components/AMPageShell';

import { ASSETS } from './data/assetData';

const STATUS_COLOURS = {
  Delivered: 'bg-success/10 text-success',
  'In progress': 'bg-primary/10 text-primary',
  Planned: 'bg-muted text-muted-foreground',
  Market: 'bg-[rgba(59,76,76,0.07)] text-blue-700',
  'Not viable': 'bg-error/10 text-error',
};

export default function ValueCreation({ selectedAssets, showToast }) {
  const assets = selectedAssets || ASSETS;
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [activeAssetId, setActiveAssetId] = useState(assets?.[0]?.id);
  const asset = assets?.find((a) => a?.id === activeAssetId) || assets?.[0];

  const allActions = assets?.flatMap((a) => [
    ...a?.valuationCreationActions?.map((ac) => ({ ...ac, assetName: a?.name, sector: a?.sector, type: 'Acquisition' })),
    ...a?.valuationPostActions?.map((ac) => ({ ...ac, assetName: a?.name, sector: a?.sector, type: 'Post-acquisition' })),
  ]);

  const filteredPortfolio = allActions?.filter((ac) => {
    if (filterStatus && ac?.status !== filterStatus) return false;
    if (filterSector && ac?.sector !== filterSector) return false;
    return true;
  });

  const sectors = [...new Set(assets.map((a) => a.sector))];
  const statuses = Object.keys(STATUS_COLOURS);

  if (!asset) return <AMPageShell activeTab="/asset-management/value-creation"><p className="text-muted-foreground font-caption">No assets selected.</p></AMPageShell>;

  return (
    <AMPageShell activeTab="/asset-management/value-creation">
      <div className="space-y-8">
        {/* Per-asset view */}
        <div className="flex gap-6">
          <div className="w-48 flex-shrink-0">
            <div className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">Asset</p>
              </div>
              {assets?.map((a) => (
                <button key={a?.id} onClick={() => setActiveAssetId(a?.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm font-caption transition-colors ${
                    a?.id === activeAssetId ? 'bg-primary/5 text-primary font-medium border-l-2 border-primary' : 'text-foreground hover:bg-muted/40'
                  }`}>
                  {a?.name}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-0 space-y-4">
            {[{ label: 'Value Creation Actions — Identified at Acquisition', data: asset?.valuationCreationActions },
              { label: 'Value Creation Actions — Identified Post-Acquisition', data: asset?.valuationPostActions }]?.map(({ label, data }) => (
              <div key={label} className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
                <div className="px-6 py-4 border-b border-border">
                  <h3 className="font-heading font-semibold text-foreground" style={{ fontSize: '1rem' }}>{label}</h3>
                </div>
                {data?.length === 0 ? (
                  <p className="px-6 py-4 font-caption text-sm text-muted-foreground">No actions recorded.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border bg-muted/30">
                          {['Action', 'Status', 'Owner', 'Target Date', 'Value Impact (£m)', 'Notes']?.map((h) => (
                            <th key={h} className="px-4 py-3 text-left font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data?.map((ac) => (
                          <tr key={ac?.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                            <td className="px-4 py-3 font-caption text-sm font-medium text-foreground">{ac?.action}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-medium ${STATUS_COLOURS?.[ac?.status] || 'bg-muted text-muted-foreground'}`}>{ac?.status}</span>
                            </td>
                            <td className="px-4 py-3 font-caption text-sm text-foreground">{ac?.owner}</td>
                            <td className="px-4 py-3 font-data text-sm text-foreground">{ac?.targetDate}</td>
                            <td className="px-4 py-3 font-data text-sm font-semibold text-foreground">{ac?.valueImpact}</td>
                            <td className="px-4 py-3 font-caption text-sm text-muted-foreground">{ac?.notes}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Portfolio-level view */}
        <div className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center gap-4 flex-wrap">
            <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.1rem' }}>Portfolio Value Creation Register</h2>
            <div className="flex items-center gap-2 ml-auto">
              <select value={filterStatus} onChange={(e) => setFilterStatus(e?.target?.value)}
                className="text-xs px-2 py-1.5 border border-border rounded bg-card text-foreground font-caption">
                <option value="">All statuses</option>
                {statuses?.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={filterSector} onChange={(e) => setFilterSector(e?.target?.value)}
                className="text-xs px-2 py-1.5 border border-border rounded bg-card text-foreground font-caption">
                <option value="">All sectors</option>
                {sectors?.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  {['Asset', 'Sector', 'Type', 'Action', 'Status', 'Owner', 'Target Date', 'Value Impact (£m)']?.map((h) => (
                    <th key={h} className="px-4 py-3 text-left font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPortfolio?.map((ac) => (
                  <tr key={ac?.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-3 font-caption text-sm font-medium text-foreground whitespace-nowrap">{ac?.assetName}</td>
                    <td className="px-4 py-3 font-caption text-xs text-muted-foreground">{ac?.sector}</td>
                    <td className="px-4 py-3 font-caption text-xs text-muted-foreground">{ac?.type}</td>
                    <td className="px-4 py-3 font-caption text-sm text-foreground">{ac?.action}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-medium ${STATUS_COLOURS?.[ac?.status] || 'bg-muted text-muted-foreground'}`}>{ac?.status}</span>
                    </td>
                    <td className="px-4 py-3 font-caption text-sm text-foreground">{ac?.owner}</td>
                    <td className="px-4 py-3 font-data text-sm text-foreground">{ac?.targetDate}</td>
                    <td className="px-4 py-3 font-data text-sm font-semibold text-foreground">{ac?.valueImpact}</td>
                  </tr>
                ))}
                {filteredPortfolio?.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center font-caption text-sm text-muted-foreground">No actions match filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AMPageShell>
  );
}
